// Shrinking a photo in the browser before it is uploaded — the question bank's
// images and a blog post's images.

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("unreadable")); };
    img.src = url;
  });

const toBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

/**
 * A photo over the limit is the usual case (a phone camera's 3–5 MB), so it is
 * resized rather than refused: longest side to 1600px, then smaller. A JPEG has
 * no transparency and stays a JPEG; a PNG or WebP may have some, so it becomes
 * a WebP, which keeps it (a JPEG would turn the clear parts black). A GIF is
 * never re-encoded — that would drop its animation.
 *
 * @returns {Promise<File|null>} a file within `maxBytes`, or null when it cannot be made to.
 */
export async function shrinkImageToFit(file, maxBytes) {
  if (file.type === "image/gif") return null;
  const type = file.type === "image/jpeg" ? "image/jpeg" : "image/webp";
  const img = await loadImage(file);
  for (const [side, quality] of [[1600, 0.85], [1280, 0.78], [1024, 0.72]]) {
    const scale = Math.min(1, side / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await toBlob(canvas, type, quality);
    // A browser that cannot write WebP hands back a PNG instead, which is no use.
    if (!blob || blob.type !== type) return null;
    if (blob.size <= maxBytes) {
      const name = file.name.replace(/\.[^.]+$/, "") || "image";
      return new File([blob], `${name}.${type === "image/jpeg" ? "jpg" : "webp"}`, { type });
    }
  }
  return null;
}
